"use client";

import { useAuthProfile } from "@/hooks/use-auth-profile";
import { updateViewPreference } from "@/lib/profile-service";
import type { ViewPreference } from "@/lib/types";
import { useCallback, useEffect, useState } from "react";

const DEFAULT_VIEW: ViewPreference = "card";
const STORAGE_KEY = "ezrecipebox.viewPreference";

/**
 * Get view preference from localStorage synchronously
 */
function getStoredViewPreference(): ViewPreference {
  if (typeof window === "undefined") {
    return DEFAULT_VIEW;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "card" || stored === "list") {
      return stored;
    }
  } catch {
    // Ignore localStorage errors (e.g., in private browsing)
  }
  return DEFAULT_VIEW;
}

/**
 * Store view preference in localStorage
 */
function setStoredViewPreference(view: ViewPreference): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, view);
  } catch {
    // Ignore localStorage errors (e.g., in private browsing)
  }
}

/**
 * Hook to manage user's view preference (card or list)
 * Uses localStorage for immediate access, syncs with Firestore profile
 */
export function useViewPreference() {
  const { user, profile } = useAuthProfile();
  
  // Initialize from localStorage immediately (synchronous)
  const [viewPreference, setViewPreferenceState] = useState<ViewPreference>(getStoredViewPreference);

  // Sync with Firestore profile when it loads
  useEffect(() => {
    if (profile?.viewPreference) {
      const profileView = profile.viewPreference;
       
      setViewPreferenceState(profileView);
      // Update localStorage to match Firestore (in case it was changed elsewhere)
      setStoredViewPreference(profileView);
    }
  }, [profile?.viewPreference]);

  const setViewPreference = useCallback(
    async (view: ViewPreference) => {
      // Capture current value for error recovery
      const previousValue = viewPreference;
      
      // Update local state immediately for instant UI feedback
      setViewPreferenceState(view);
      setStoredViewPreference(view);

      if (!user) {
        return;
      }
      try {
        await updateViewPreference(user, view);
      } catch (error) {
        console.error("Failed to update view preference", error);
        // Revert to previous value on error
        setViewPreferenceState(previousValue);
        setStoredViewPreference(previousValue);
      }
    },
    [user, viewPreference],
  );

  return {
    viewPreference,
    setViewPreference,
  };
}

